import {noop} from '@priority-software/priority-style-react';
import {common, priority} from '@priority-software/priority-proto-api/lib';

import {
    batch,
    endForm,
    fetchColumns,
    fetchCount,
    fetchFormActions,
    fetchFormData,
    fetchSubForms,
    fieldUpdate,
    formSystemConstants,
    getFormCustomData,
    getStatuses,
    htmlSignature,
    noDefaultMessageHandling,
    recordEdit,
    recordSave,
    recordUndo,
    saveHTML,
    setFormCustomData,
    startForm
} from '../../../services/form/form';
import {
    endEntityBySession,
    getDname,
    handleFormFatalError,
    isCurrentEntityReactForm
} from '../../../_shared/GWTBridge/invoke/genPage';
import {getKeyByLang} from '../utils/custom-properties';
import {SavedSearch} from '../types';
import messagesText from '../../../_shared/Messages/texts';
import {
    handleHTMLFormOpenMessages,
    handleHTMLLockedState,
    handleStartSubFormError,
    showFormClientSideMessage
} from './messages';
import {handleScreenMessage} from './screen';
import {CachedFields} from '../../../stores/Form/FormsCache';
import {Options} from '../../../services/proto/core/server';
import {getUpdateActionType} from '../../../stores/Form/utils';
import {isRTL} from '../../../Localization';
import {serializeSearches} from './utils/filter';
import {
    getResentSearches,
    getSavedSearches
} from '../components/AdvancedSearch/AdvancedSearchDrawer/components/SavedSearches/service';
import {executeSearch} from './searches';
import {fetchSubformData} from './batchedActions/fetchSubformData';
import {fetchMainFormData} from './batchedActions/fetchMainFormData';
import {fetchHTMLData} from './batchedActions/fetchHTMLForm';
import {getHtmlContent} from '../../../utils/lexical/getHtmlContent';
import {createSubFormInstance} from './utils/createFormInstance';
import {FormMetaStoreKeys} from '../../../stores/Form/enums';
import {BI_mock_data} from './mock';
import {Form, FormViewEnum} from '../../../stores/Form/Form';
import {formsStore} from '../../../stores/Form/FormsStore';
import {Record} from '../../../stores/Form/Record';
import {APIActionStatus, Screen} from '../../../stores/Form/Screen';
import {PriKeys} from '../consts/priKeys';
import {
    fetchActionsDesign,
    fetchRowViewColumnsDesign,
    fetchSubformsDesign,
    fetchTableViewColumnsDesign
} from '../../FormDesign/service';
import {removeLiValueAttributes} from '../utils/html';
import {setNFormFatalError} from '../../../_shared/GWTBridge/invoke/netForm';
import {runInAction} from 'mobx';
import {updateAllRecordsCount} from './utils/updateAllRecordsCount';
import {PAGE_LIMIT} from '../consts/pagination';
import {createFiltersFromFilterParams, formatFilterValue} from './filters';
import {requestPaginationData} from './pagination';
import {formsStore as legacyStore} from '../../../_legacy/Form/stores/FormsStore';

export const setFormSystemConstants = async (dname?: string) => {
    if (!formsStore.sysConstants) {
        try {
            const sysConstants = await formSystemConstants(dname);
            formsStore.setSystemConstants(sysConstants.SystemConstants);
        } catch {
            return false;
        }
    }
    return true;
};

export const requestFormDesign = async (formName: string, rootFormName: string) => {
    const res = await Promise.all([
        fetchTableViewColumnsDesign(formName, rootFormName),
        fetchRowViewColumnsDesign(formName, rootFormName),
        fetchActionsDesign(formName, rootFormName),
        fetchSubformsDesign(formName, rootFormName)
    ]);

    const [
        tableViewColumnsDesignRes,
        rowViewColumnsDesignRes,
        actionsDesignRes,
        subformsDesignRes
    ] = res;

    return {
        tableViewColumnsDesign: tableViewColumnsDesignRes?.ColumnsDesign || [],
        rowViewColumnsDesign: rowViewColumnsDesignRes?.TabsDesign || [],
        actionsDesign: actionsDesignRes?.ActionsDesign || [],
        subformsDesign: subformsDesignRes?.SubformsDesign || [],
        showPicture:
            rowViewColumnsDesignRes?.Picture === priority.netitems.form.Picture.VisiblePicture ||
            false
    };
};

export const refreshFormDesign = async (form: Form) => {
    const designData = await requestFormDesign(form.ename, form?.parentForm?.ename || form.ename);

    const {formDesign} = form;

    if (formDesign) {
        runInAction(() => {
            formDesign.togglePicture(designData.showPicture);
            formDesign.setRowViewColumnsDesign(designData.rowViewColumnsDesign);
            formDesign.setTableColumnsDesign(designData.tableViewColumnsDesign);
            formDesign.setActionsDesign(designData.actionsDesign);
            formDesign.setSubformsDesign(designData.subformsDesign);
        });
    }
};

export const openForm = async (
    formName: string,
    runDefaultSearch: boolean = true,
    dname: string = getDname()
): Promise<Form | null> => {
    const startRs: priority.netitems.form.StartRs = await startForm(
        formName,
        noDefaultMessageHandling,
        null,
        dname
    );

    const isFormOk = await handleScreenMessage(startRs);

    if (isFormOk) {
        const responses = await Promise.all([
            fetchMainFormData(startRs.Session, formName),
            getFormAdditionalData(formName),
            requestFormDesign(formName, formName)
        ]);

        const [columnsRs, subFormsRs, actionsRs] = responses[0];
        const formCustomData = responses[1];
        const formDesignData = responses[2];

        if (columnsRs) {
            const isColumnOk = await handleScreenMessage(columnsRs);

            if (isColumnOk) {
                // Create form instance
                const form: Form = new Form(
                    formName,
                    startRs,
                    columnsRs,
                    0,
                    formCustomData,
                    formDesignData,
                    dname
                );
                // Request column statuses
                await getFormStatuses(form);
                // Request and set available subforms;
                if (subFormsRs) {
                    await handleScreenMessage(subFormsRs, {}, async () => {
                        form.setSubForms(subFormsRs.SubForms);
                    });
                }
                // Request and set form actions
                if (actionsRs) {
                    await handleScreenMessage(actionsRs, {}, async () => {
                        form.setFormActions(actionsRs.Actions);
                    });
                }
                // Request and set form saved searches
                await getSavedSearchesAction(form, runDefaultSearch);

                return form;
            }
        }
    }
    return null;
};

export const getSubformsList = async (session: string, formName: string) => {
    const {cachedData} = formsStore;

    const cachedSubForms = cachedData.getData(formName, CachedFields.subformsList);

    if (!cachedSubForms) {
        const res = await fetchSubForms(session);
        cachedData.setData(formName, CachedFields.subformsList, res.SubForms);

        return res.SubForms;
    }
    return cachedSubForms;
};

export const getColumns = async (Session: string, formName: string, options?: Partial<Options>) => {
    const {cachedData} = formsStore;
    // return saved value if it's in the formstore
    const cachedColumns = cachedData.getData(formName, CachedFields.columns);
    if (!cachedColumns) {
        // send a request and save response the formstore
        const columnsRs = await fetchColumns(Session, options);
        cachedData.setData(formName, CachedFields.columns, columnsRs);

        return columnsRs;
    }

    return cachedColumns;
};

export const getFormAdditionalData = async (formName: string) => {
    const {cachedData} = formsStore;
    let formCustomData = cachedData.getData(formName, CachedFields.formMetaData);

    if (!formCustomData) {
        const keys = [getKeyByLang(formName, 'CUSTOM_DATA', '*')];
        formCustomData = await getFormCustomData(keys, false, true);

        cachedData.setData(formName, CachedFields.formMetaData, formCustomData);
    }

    return formCustomData;
};

export const openSubForm = async (
    parentForm: Form,
    formName: string,
    isHTMLForm?: boolean
): Promise<APIActionStatus> => {
    let isOk;
    if (isHTMLForm) {
        isOk = await openHTMLSubform(parentForm, formName);
    } else {
        isOk = await fetchSubFormData(parentForm, formName, parentForm.currentRow);
    }

    if (isOk) {
        formsStore.currentScreen.setActiveLevel(parentForm.level + 1);
        formsStore.currentScreen.setBreadcrumbs();
    }

    return isOk;
};

export const startRecordEdit = async (form: Form, options: Partial<Options>) => {
    const ScrLine = form.currentRow;
    const Keys = form.records[ScrLine]?.keys;
    return await recordEdit(form.session, {Keys}, options);
};

export const startRecordUndo = async (form: Form) => {
    const recordUpdateRes = await recordUndo(form.session, {
        Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
    });
    if (form.currentRecord.isNew) {
        form.resetRecord(form.currentRow);
    } else {
        form.updateRecordValues(form.currentRow, recordUpdateRes.Record.Fields);
    }
};

export const saveRecordValue = async (
    session: string,
    options: Partial<Options>
): Promise<priority.netitems.form.RecordSaveRs> => {
    return await recordSave(session, options);
};

export const closeFormBySession = async (session: string, showExitMessage: boolean = true) => {
    const screen = formsStore.getScreenBySession(session);
    if (screen) {
        const valueActionType = getUpdateActionType(screen.getActiveForm(), true, screen.editMode, {
            isExit: true
        });
        const isValidAction = await screen.checkAndSave(valueActionType);

        if (isValidAction) {
            if (showExitMessage && formsStore.sysConstants.exitformconfirm) {
                const screen = formsStore.getScreenBySession(session);
                if (screen.form.records?.length) {
                    showFormClientSideMessage(
                        session,
                        priority.netitems.shared.Message.Type.Warning,
                        {
                            title: messagesText.exitForm,
                            onConfirm: () => deleteForm(session, false)
                        }
                    );
                    return APIActionStatus.passed;
                }
            }
            await deleteForm(session);
            return APIActionStatus.passed;
        }
        return APIActionStatus.blocked;
    }
    return APIActionStatus.blocked;
};

const deleteForm = async (session: string, clearCurrentSession: boolean = true) => {
    const screen = formsStore.getScreenBySession(session);

    await endForm(session);
    const zoomSource = screen.zoomSource;

    formsStore.deleteForm(session, clearCurrentSession);
    if (!!zoomSource) {
        zoomSource.setZoomChild(null);
        formsStore.setCurrentSession(zoomSource.session);
    } else {
        endEntityBySession(session);
    }
};

export const updateFieldValue = async (form: Form, Field: priority.netitems.form.IField | null) => {
    return await fieldUpdate(form.session, Field);
};

export const exitSubForm = async (session: string) => {
    return await endForm(session);
};

const getActiveZoomChild = (currentScreen: Screen) => {
    if (currentScreen.zoomChild) {
        return getActiveZoomChild(currentScreen.zoomChild);
    }
    return currentScreen;
};

export const setActiveFormBySession = (session: string, background: boolean = false) => {
    let nextSession = session;
    if (!background) {
        if (session) {
            const activeScreen = formsStore.getScreenBySession(session);
            if (activeScreen.zoomChild) {
                const activeZoomChild = getActiveZoomChild(activeScreen.zoomChild);
                nextSession = activeZoomChild.session;
            }
        }
    }
    formsStore.setCurrentSession(nextSession);
    formsStore.onBackground = background;
};

export const setCurrentFieldValue = (value: string) => {
    formsStore.getActiveForm()?.activeField().setCurrentValue(value);
};

export const isCurrentSession = (session: string) => {
    return session === formsStore.currentSession;
};

export const setFormActions = async (form: Form) => {
    const {cachedData} = formsStore;
    let actions = cachedData.getData(form.ename, CachedFields.actions);

    if (!actions) {
        const actionRS: priority.netitems.form.ActionsRs = await fetchFormActions(form.session);
        cachedData.setData(form.ename, CachedFields.actions, actionRS?.Actions);
        actions = actionRS.Actions;
    }
    form.setFormActions(actions);
};

export const getFormStatuses = async (form: Form) => {
    const hasStatuses = form?.columns.find(c => c.IsStatus);

    if (hasStatuses) {
        const {cachedData} = formsStore;
        const cachedStatuses = cachedData.getData(form.ename, CachedFields.statuses);

        if (!cachedStatuses) {
            const statusesRs = await getStatuses(form.ename);

            if (statusesRs?.Statuses?.length > 0) {
                cachedData.setData(form.ename, CachedFields.statuses, statusesRs.Statuses);
                form.setStatuses(statusesRs.Statuses);
            }
        } else {
            form.setStatuses(cachedStatuses);
        }
    }
};

export const setLegacyFormSidePanelSubforms = (value: {[key: string]: string}) => {
    formsStore.setLegacyFormSidePanelSubforms(value);
};

export const setDesignPanelOpen = (value: boolean) => {
    formsStore.designPanelOpened = value;
};

export const getHTMLSignature = async () => {
    const signatureData = formsStore.htmlSignature;
    if (!signatureData) {
        const res = await htmlSignature();
        formsStore.htmlSignature = isRTL() ? res.Signautre : res.SignatureEng;
    }

    return formsStore.htmlSignature;
};

export const startRecordDelete = async (record: Record, callback: () => void = noop) => {
    showFormClientSideMessage(
        record.parentForm.session,
        priority.netitems.shared.Message.Type.Warning,
        {
            title: 'Delete?',
            content: messagesText.deleteData,
            onConfirm: async () => {
                const isOk = await formsStore.currentScreen.recordDeleteAction(record);
                if (isOk) await callback();
            }
        }
    );
};

export const getSavedSearchesAction = async (
    form: Form,
    runDefaultSearch: boolean = true
): Promise<boolean> => {
    const searches = await getSavedSearches(form?.ename);

    const serializedSearches: SavedSearch[] = searches ? serializeSearches(searches) : [];
    form?.searchStore.setFormSavedSearches(serializedSearches);

    const defaultSearch = serializedSearches.find(search => search.isDefault);

    if (runDefaultSearch && defaultSearch) {
        await executeSearch(form, defaultSearch); //prevent execute  default search - issue with empty op
        return true;
    }
    return false;
};

export const getRecentSearchesAction = async (form: Form) => {
    const searches = await getResentSearches(form.ename);

    const serializedSearches: SavedSearch[] = searches ? serializeSearches(searches) : [];
    form.searchStore.setRecentSearches(serializedSearches);
};

export const sendBatch = async (
    session: string,
    requests,
    options?: Partial<Options>
): Promise<priority.netitems.form.BatchRs> => {
    const batchTasks: priority.netitems.form.Grpc2Net[] = requests.map(
        ({key, request}) => new priority.netitems.form.Grpc2Net({[key]: request})
    );

    return await batch(batchTasks, session, options);
};

export const fetchSubFormData = async (
    parentForm: Form,
    formName: string,
    recordIndex: number,
    prefetchData: boolean = false
) => {
    const keys = parentForm.records[recordIndex].keys;

    if (keys) {
        const searches = await getSavedSearches(formName);

        const serializedSearches: SavedSearch[] = searches ? serializeSearches(searches) : [];
        const defaultSearch = serializedSearches.find(search => search.isDefault);

        const responses = await Promise.all([
            fetchSubformData(parentForm.session, formName, keys, {Filters: defaultSearch?.filters}),
            getFormAdditionalData(formName),
            requestFormDesign(formName, parentForm.ename)
        ]);

        const [startSubFormRes, columnsRs, subFormsRs, actionsRs, fetchRs] = responses[0];
        const formCustomData = responses[1];
        const designData = responses[2];
        const isFormOk = await handleStartSubFormError(startSubFormRes, parentForm);
        const subFormLevel = parentForm.level + 1;

        if (isFormOk) {
            const isColumnsOk = await handleScreenMessage(columnsRs);

            if (columnsRs && isColumnsOk) {
                const subForm = createSubFormInstance(parentForm.session, {
                    formName,
                    startSubFormRes,
                    columnsRs,
                    subFormLevel,
                    formCustomData,
                    designData
                });

                if (subFormsRs) {
                    await handleScreenMessage(subFormsRs);
                    subForm.setSubForms(subFormsRs.SubForms);
                }

                if (actionsRs) {
                    await handleScreenMessage(subFormsRs);
                    subForm.setFormActions(actionsRs.Actions);
                }

                return await handleScreenMessage(fetchRs, {}, async () => {
                    subForm.setParentForm(parentForm);
                    subForm.setRecords(fetchRs?.Records || []);

                    await updateAllRecordsCount(subForm, fetchRs?.Records?.length || 0, {
                        top: PAGE_LIMIT,
                        skip: 0
                    });
                    defaultSearch && subForm.updateFilters(defaultSearch.filters);

                    await getFormStatuses(subForm);

                    if (prefetchData) {
                        await endForm(subForm.session);
                    }
                });
            }
        }
    }
    return APIActionStatus.blocked;
};

export const openHTMLSubform = async (
    parentForm: Form,
    formName: string,
    prefetchData: boolean = false
) => {
    const formLevel = parentForm.level + 1;
    const response = await Promise.all([
        fetchHTMLData(parentForm.session, formName, parentForm.currentRecord?.keys, prefetchData),
        getHTMLSignature()
    ]);
    const [htmlFormRs, htmlFetchRs, endRs] = response[0];
    const isFormOk = await handleHTMLFormOpenMessages(htmlFormRs, parentForm);

    if (isFormOk) {
        const subForm = new Form(formName, htmlFormRs, null, formLevel);
        const allowLevelSwitch = await handleHTMLLockedState(subForm, prefetchData);

        if (htmlFetchRs) {
            const isHTMLRsOk = await handleScreenMessage(htmlFetchRs);
            if (htmlFetchRs.Content) {
                subForm.setOriginHTMLContent(htmlFetchRs.Content);
            }

            subForm.setParentForm(parentForm);

            if (!prefetchData) {
                if (allowLevelSwitch) {
                    return isHTMLRsOk;
                }
                formsStore.currentScreen.setActiveLevel(parentForm.level);
                return allowLevelSwitch;
            }

            if (endRs) {
                return await handleScreenMessage(endRs);
            }
        }
    }

    return APIActionStatus.blocked;
};

export const prefetchSubformData = async (form: Form, recordIndex: number = 0) => {
    const prefetchSubFormName = form.activeSubForm?.ename || form.subFormsList?.[0]?.Ename;
    const isHtml = form.activeSubForm?.form?.Ishtml || form.subFormsList?.[0]?.IsText;

    if (prefetchSubFormName) {
        if (isHtml) {
            return await openHTMLSubform(form, prefetchSubFormName, true);
        }
        return await fetchSubFormData(form, prefetchSubFormName, recordIndex, true);
    }
    return APIActionStatus.blocked;
};

export const prefetchSubFormDataOnRowChange = async (form: Form, rowIdx: number) => {
    const isHtml = form.activeSubForm?.form?.Ishtml || form.subFormsList?.[0]?.IsText;
    const canPreFetch = !formsStore.isRecording && !form.searchModeLevel && !isHtml;

    if (canPreFetch) {
        return await prefetchSubformData(form);
    }
    return APIActionStatus.blocked;
};

export const checkAndSaveHtml = async (form: Form) => {
    const editorInstance = form._htmlEditorInstance;
    if (!form.lockBy) {
        const editorContent = await getHtmlContent(editorInstance, null, isRTL());

        if (editorContent !== form.originHTMLContent) {
            const cleaned = removeLiValueAttributes(editorContent);
            const htmlSaveRs = await saveHTML(form.session, cleaned);

            return await handleScreenMessage(htmlSaveRs, {}, () => {
                form.setOriginHTMLContent(editorContent as string);
            });
        }
    }
    return APIActionStatus.passed;
};

export const updateCustomColumnWidth = async (form: Form, columnId: number, width: number) => {
    form.customColumnsWidth = {...form.customColumnsWidth, [columnId]: width};

    await setFormCustomData(
        {
            [FormMetaStoreKeys.customColumnsWidth]: new common.Constant({
                Str: JSON.stringify(form.customColumnsWidth)
            })
        },
        form.ename,
        false,
        true
    );
};

export const updateTableHeight = async (form: Form, height: number) => {
    form.tableHeight = height;

    await setFormCustomData(
        {
            [`${FormMetaStoreKeys.tableHeight}:${form.level}`]: new common.Constant({Int: height})
        },
        form.ename,
        false,
        true
    );
};

export const updateFormView = async (form: Form, formView: FormViewEnum) => {
    runInAction(() => {
        form.view = formView;
    });

    await setFormCustomData(
        {[`${FormMetaStoreKeys.formView}`]: new common.Constant({Str: formView})},
        form.ename,
        false,
        true
    );
};

export const getBIData = async (session: string, colId: number) => {
    return new Promise(resolve => {
        const form = formsStore.getScreenBySession(session)?.getActiveForm();

        if (form && form.records[form.currentRow]?.keys) {
            setTimeout(() => resolve(BI_mock_data), 200);
        } else {
            resolve(null);
        }
    });
};

export const getAllRecordsCount = async (form: Form) => {
    const {session, filters} = form;

    const res = await fetchCount(session, filters);
    const count = Math.max((res.recordCount as number) - form.hiddenRecords.length, 0);

    if (!res.Messages.length) {
        form.setAllRecordsCount(count);
    }
    return count;
};

export const canExecuteCommand = cmdId => {
    // TODO: The conditions should be updated when the specific development is done
    // Meanwhile I check if filled with saved records
    // See enable/disable logic  NetForm.java function CanExecute line: 7976
    switch (cmdId) {
        case PriKeys.AttachImport:
        case PriKeys.AttachActivateApp:
        case PriKeys.MailTaskUsers:
        case PriKeys.SetFollowup:
        case PriKeys.MailInterface:
            return false;
        case PriKeys.SendExcel:
        case PriKeys.SendWord:
        case PriKeys.Clnln:
            return !formsStore.currentForm?.isEmpty;
        case PriKeys.SendLink:
            return !formsStore.currentForm?.currentRecord?.isNew;
        case PriKeys.NoOfRows:
            // Enable number of rows action in the general menu only for old UI.
            return !!legacyStore.currentSession;
        case PriKeys.App:
        case PriKeys.Navigate:
            return true;
        default:
            return true;
    }
};

export const executeCommand = cmdId => {
    console.log(cmdId);
};

export const clearFormEntity = async (session: string) => {
    // Clear and close the form entity from the store (used in critical error handling)
    const screen = formsStore.getScreenBySession(session);
    if (screen) {
        const zoomSource = screen?.zoomSource;

        if (!!zoomSource) {
            // Handle Zoom Form Case:
            // remove spoiled session screen and switch to zoom parent form
            zoomSource.setZoomChild(null);
            formsStore.setCurrentSession(zoomSource.session); // switch to parent source form
            formsStore.screens.delete(session); // remove zoom screen
        } else {
            // Handle Basic Forms:
            // endEntityBySession triggers under the hood deleteFormMethod from Screen store
            const isEntityClosedInGWT = endEntityBySession(session);
            if (!isEntityClosedInGWT) {
                formsStore.currentSession = null;
            }
        }
    }

    try {
        if (!isCurrentEntityReactForm()) {
            setNFormFatalError();
            handleFormFatalError();
        }
    } catch (error) {
        console.error('Something went wrong with Form on GWT side:', error);
    }
};

export const refreshCurrentRow = async (
    record: Record,
    options?: Partial<Options>
): Promise<boolean> => {
    if (!record || !record.parentForm || !record.keys) {
        console.warn('refreshCurrentRow: Invalid record or missing keys');
        return false;
    }

    const {parentForm} = record;
    const {session} = parentForm;

    try {
        let filters = [];
        record.parentForm.keyColumns.forEach(col => {
            const filtersArr = createFiltersFromFilterParams([
                {
                    fieldId: col.id,
                    value: formatFilterValue(
                        record.parentForm,
                        col.id,
                        record.getField(col.id)?.origValue?.toString() ?? ''
                    )
                }
            ]);
            filters = [...filters, ...filtersArr];
        });

        const data: priority.netitems.form.FetchRs = await fetchFormData({
            session,
            filters,
            attachments: true,
            filtersOperator: priority.netitems.shared.LogicalOperator.And,
            options: options
        });

        // Handle the response and update the record
        let updateSuccess = false;
        const isAllowed = await handleScreenMessage(data, {}, () => {
            if (data.Records?.length > 0) {
                const freshRecord = data.Records[0];
                // Update the specific record with fresh data
                parentForm.updateRecordValues(record.recordIndex, freshRecord.Fields, record.isNew);
                updateSuccess = true;
            } else {
                console.warn('refreshCurrentRow: No record found with the specified keys');
            }
        });

        return isAllowed && updateSuccess;
    } catch (error) {
        console.error('refreshCurrentRow: Error refreshing record', error);
        return false;
    }
};

export const refreshByRefreshLines = async (
    form: Form,
    refreshLinesCode: priority.netitems.form.RefreshLines
) => {
    if (refreshLinesCode === priority.netitems.form.RefreshLines.ActiveRecord) {
        await refreshCurrentRow(form.currentRecord);
    } else if (refreshLinesCode === priority.netitems.form.RefreshLines.AllRecords) {
        await requestPaginationData(form, PAGE_LIMIT, form.paginationOffset, form.currentRow);
    }
};
